import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedDiagnosticDepartments,
  useRestoreDiagnosticDepartments,
  usePermanentlyDeleteDiagnosticDepartments,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AlertDialog } from '../../components/ui/AlertDialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Building2,
} from 'lucide-react';

const LIMIT = 20;

export function DiagnosticDepartmentsTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; department?: any } | null>(null);
  const [blockedDialog, setBlockedDialog] = useState<{ title: string; messages: string[] } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedDiagnosticDepartments(page, LIMIT, debouncedSearch.trim() || undefined);
  const restoreDepartments = useRestoreDiagnosticDepartments();
  const permanentDelete = usePermanentlyDeleteDiagnosticDepartments();

  const departments = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const departmentName = (department: any) => department?.name || department?.code || 'Unknown';

  const allSelected = departments.length > 0 && departments.every((d: any) => selectedIds.includes(d.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !departments.some((d: any) => d.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...departments.map((d: any) => d.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      const res: any = await restoreDepartments.mutateAsync([id]);
      const blocked = res?.data?.blocked ?? [];
      if (blocked.length > 0) {
        toast.error(blocked[0]?.reason || 'This department could not be restored');
        return;
      }
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Department restored');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore department');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restoreDepartments.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      if (restored > 0) toast.success(`Restored ${restored} department${restored === 1 ? '' : 's'}`);
      if (blocked.length > 0) {
        toast.error(`${blocked.length} department(s) could not be restored: ${blocked[0]?.reason ?? ''}`);
      } else if (restored === 0) {
        toast.error('No departments were restored');
      }
      setSelectedIds([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore departments');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        const res: any = await permanentDelete.mutateAsync([confirmTarget.department.id]);
        const blocked = res?.data?.blocked ?? [];
        if (blocked.length > 0) {
          setBlockedDialog({
            title: 'Cannot delete permanently',
            messages: [
              `${departmentName(confirmTarget.department)} — ${blocked[0]?.reason ?? 'This department could not be permanently deleted.'}`,
            ],
          });
          return;
        }
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.department.id));
        toast.success('Department permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? 0;
        const blocked = res?.data?.blocked ?? [];
        if (deleted > 0) toast.success(`Permanently deleted ${deleted} department${deleted === 1 ? '' : 's'}`);
        if (blocked.length > 0) {
          const nameById = new Map<string, string>(departments.map((d: any) => [d.id, departmentName(d)]));
          setBlockedDialog({
            title: `Cannot permanently delete ${blocked.length} department${blocked.length === 1 ? '' : 's'}`,
            messages: blocked.map((b: any) => `${nameById.get(b.id) ?? b.id} — ${b.reason}`),
          });
        } else if (deleted === 0) {
          setBlockedDialog({
            title: 'No departments were deleted',
            messages: ['None of the selected departments could be permanently deleted.'],
          });
        }
        setSelectedIds([]);
      }
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete permanently',
        messages: [err?.response?.data?.message || 'The department(s) could not be permanently deleted.'],
      });
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = restoreDepartments.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/departments')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Departments Trash</h1>
            <p className="text-xs text-text-muted">Restore departments or delete them permanently</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed departments..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restoreDepartments.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed departments</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={departments.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {departments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Building2 className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed departments match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  departments.map((department: any) => (
                    <tr key={department.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(department.id)}
                          onChange={() => toggleSelectOne(department.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text">{departmentName(department)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-text">{department.code || '—'}</td>
                      <td className="px-4 py-3 text-sm text-text-muted max-w-xs truncate">
                        {department.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {department.deletedAt ? new Date(department.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRestore(department.id)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', department })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed departments"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete departments permanently' : 'Delete department permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected department(s)? This cannot be undone. Linked employees, appointments, visits and expenses will simply be unassigned.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.department ? departmentName(confirmTarget.department) : ''}</strong>?
              This cannot be undone. Linked employees, appointments, visits and expenses will simply be unassigned.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <AlertDialog
        open={blockedDialog !== null}
        title={blockedDialog?.title ?? 'Cannot delete permanently'}
        messages={blockedDialog?.messages}
        onClose={() => setBlockedDialog(null)}
      />
    </div>
  );
}
